import React, {useCallback} from 'react';
import {Icon} from 'shared/helpers';
import {useVariationRequest} from 'components/customer/JobDashboard/helpers/useVariationRequest';
import {useDialogContext} from 'contexts/DialogContext';
import {useJobContext} from 'contexts/JobContext';
import {VariationRequestForm} from 'components/customer/JobDashboard/columns/VariationRequestForm';
import {PartialJob} from 'shared/types/PartialJob';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {Product} from 'components/customer/Product/entity/Product';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {
    CommentBox,
    RelativeInfoBox,
    Title,
    EditComment,
    TextContainer,
    ReadMore,
} from 'components/customer/JobDashboard/columns/SharedComponents';
import {ApplianceVariation} from 'components/customer/JobDashboard/columns/ApplianceVariation';

interface VariationRequestProps {
    product: Product;
    className?: string;
}

export const VariationRequest = ({
    product,
    className,
}: VariationRequestProps) => {
    const isSmallDevice = isDeviceSmall();
    const {showAll, textContainer, showMoreButton, text, showAllToggle, lines} =
        useVariationRequest(product);
    const {showDialog} = useDialogContext();
    const {room, job} = useJobContext() as PartialJob;

    const handleEditComment = useCallback(() => {
        if (showDialog) {
            showDialog({
                title: 'Edit variation request',
                message: (
                    <VariationRequestForm product={product} roomId={room?.id} />
                ),
                hideYesButton: true,
                hideNoButton: true,
                hideFooter: true,
            });
        }
    }, [showDialog, product]);

    const preventPropagation = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    if (isSmallDevice) {
        return null;
    }

    return (
        <RelativeInfoBox className={className}>
            <ApplianceVariation product={product} />
            {(product.comment ?? '').length > 0 ? (
                <CommentBox
                    $showAll={showAll}
                    $type="primary"
                    onClick={preventPropagation}>
                    <Title>
                        Variation Request
                        {job.status == JOB_STATUS.PROCESSING ? (
                            <EditComment
                                iconName="Button-Edit.svg"
                                onClick={handleEditComment}
                            />
                        ) : null}
                    </Title>
                    <TextContainer
                        $showAll={showAll}
                        ref={textContainer}
                        $lines={lines}>
                        <div>{text}</div>
                    </TextContainer>

                    {showMoreButton || showAll ? (
                        <ReadMore onClick={showAllToggle()} $showAll={showAll}>
                            {showAll ? (
                                <Icon iconName="Button-Minus.svg" />
                            ) : (
                                <Icon iconName="Button-Plus.svg" />
                            )}
                        </ReadMore>
                    ) : null}
                </CommentBox>
            ) : null}
        </RelativeInfoBox>
    );
};
